/**
 * @file MessageList.js
 * @description This page displays a list of messages with their respective details such as message name, notificationStatus, audience, ...
 */
import '../page-list/PageList.css';
import styles from './Messages.module.scss';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { FilterModal, useFilter } from '../helpers/FilterComponent';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import {
    getMessages,
    getMessage,
    updateMessage,
    deleteMessage,
    addMessage,
} from '../../services/clientService';
import { getNameForDuplicate } from '../../utils/utils';
import { formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { useAuthContext } from '../../store/AuthContext';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import AudienceBadge from '../shared/AudienceBadge';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const MessageList = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { user } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const [isLoading, setIsLoading] = useState(true);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [messages, setMessages] = useState([]);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [viewerAudienceLabel, setViewerAudienceLabel] = useState('');
    const [togglingMessageId, setTogglingMessageId] = useState(null);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const hasViewerDataFilter =
        isAdminAudienceEnabled && Boolean(user?.jwt?.df);
    const showAudienceColumn = isAdminAudienceEnabled && !hasViewerDataFilter;

    useEffect(() => {
        loadMessages();
    }, []);

    useEffect(() => {
        const loadViewerAudience = async () => {
            if (!hasViewerDataFilter) {
                setViewerAudienceLabel('');
                return;
            }

            try {
                const populationGroups = await fetchPopulationGroups();
                const defaultGroup = populationGroups.find(
                    group => group.id === user?.jwt?.df,
                );

                setViewerAudienceLabel(
                    defaultGroup?.name || String(user?.jwt?.df || ''),
                );
            } catch (error) {
                console.error('Failed to load viewer audience', error);
                setViewerAudienceLabel(String(user?.jwt?.df || ''));
            }
        };

        loadViewerAudience();
    }, [hasViewerDataFilter, user?.jwt?.df]);

    useEffect(() => {
        const searchFiltered = messages.filter(message =>
            message.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setBaseFilteredData(searchFiltered);
    }, [searchTerm, messages]);

    const loadMessages = async () => {
        setIsLoading(true);
        try {
            const response = await getMessages(null);
            setMessages(response);
            setBaseFilteredData(response);
        } catch (error) {
            console.error('Failed to fetch messages', error);
            Toast.error(getText('msg_f973823fa185'));
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredData = messages.filter(message =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(message[key]),
            ),
        );

        switch (column) {
            case 'name':
                return Array.from(new Set(filteredData.map(item => item.name)))
                    .filter(Boolean)
                    .sort();
            case 'notificationStatus':
                return Array.from(
                    new Set(filteredData.map(item => item.notificationStatus)),
                )
                    .filter(Boolean)
                    .sort();
            case 'populationGroups':
                return Array.from(
                    new Set(
                        filteredData.flatMap(item => item.populationGroups),
                    ),
                )
                    .filter(Boolean)
                    .sort();
            case 'notificationCategory':
                return Array.from(
                    new Set(
                        filteredData.map(item => item.notificationCategory),
                    ),
                )
                    .filter(Boolean)
                    .sort();
            case 'notificationFrequency':
                return Array.from(
                    new Set(
                        filteredData.map(item => item.notificationFrequency),
                    ),
                )
                    .filter(Boolean)
                    .sort();
            default:
                return [];
        }
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
    };

    const getNextScheduledDate = message => {
        const candidate =
            message.nextScheduledFor ??
            message.nextScheduledDate ??
            message.scheduledFor;
        if (!candidate) return null;

        const date = new Date(candidate);
        return Number.isNaN(date.getTime()) || date.getTime() <= Date.now()
            ? null
            : date;
    };

    const sortedMessages = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            switch (sortConfig.key) {
                case 'nextScheduled':
                    aValue = getNextScheduledDate(a)?.getTime() || 0;
                    bValue = getNextScheduledDate(b)?.getTime() || 0;
                    break;
                case 'lastSent':
                    aValue = a.sentAt ? new Date(a.sentAt).getTime() : 0;
                    bValue = b.sentAt ? new Date(b.sentAt).getTime() : 0;
                    break;
                default:
                    aValue = aValue || '';
                    bValue = bValue || '';
                    if (typeof aValue === 'string')
                        aValue = aValue.toLowerCase();
                    if (typeof bValue === 'string')
                        bValue = bValue.toLowerCase();
            }

            return (
                (aValue < bValue ? -1 : aValue > bValue ? 1 : 0) *
                (sortConfig.direction === SortDirections.ASCENDING ? 1 : -1)
            );
        });
    }, [filteredData, sortConfig]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const renderSortArrow = key => (
        <span className={`mx-2 ${styles.sortArrowIcon}`}>
            <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                <path
                    transform={
                        sortConfig.key === key &&
                        sortConfig.direction === SortDirections.DESCENDING
                            ? 'rotate(180 5.25 3)'
                            : ''
                    }
                    d="M1 0.75L5.25 5.25L9.5 0.75"
                    stroke="#2F2B43"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );

    const handleSort = key => {
        const sortableColumns = {
            name: 'name',
            subject: 'subject',
            notificationStatus: 'notificationStatus',
            populationGroups: 'populationGroups',
            notificationCategory: 'notificationCategory',
            notificationFrequency: 'notificationFrequency',
            lastSent: 'lastSent',
            nextScheduled: 'nextScheduled',
        };

        if (key in sortableColumns) {
            setSortConfig(prevConfig => ({
                key,
                direction:
                    prevConfig.key === key &&
                    prevConfig.direction === SortDirections.ASCENDING
                        ? SortDirections.DESCENDING
                        : SortDirections.ASCENDING,
            }));
        }
    };

    const handlerRowClicked = id => {
        setRowsClicked(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id],
        );
    };

    const onToggle = async id => {
        if (togglingMessageId !== null) return;

        const message = messages.find(item => item.id === id);
        if (!message) return;

        setTogglingMessageId(id);
        try {
            const response = await getMessage(null, id);
            const updatedMessage = { ...response };
            delete updatedMessage.createdBy;
            delete updatedMessage.createdAt;
            delete updatedMessage.updatedBy;
            delete updatedMessage.updatedAt;
            updatedMessage.notificationStatusId =
                response.notificationStatusId === 1 ? 2 : 1;

            const statusName =
                updatedMessage.notificationStatusId === 1
                    ? 'enable'
                    : 'disable';
            await updateMessage(null, updatedMessage);

            const nextNotificationStatus =
                updatedMessage.notificationStatusId === 1
                    ? 'Active'
                    : 'Inactive';

            const updateMessageStatus = row =>
                row.id === id
                    ? {
                          ...row,
                          notificationStatusId:
                              updatedMessage.notificationStatusId,
                          notificationStatus: nextNotificationStatus,
                      }
                    : row;

            setMessages(prev => prev.map(updateMessageStatus));
            setBaseFilteredData(prev => prev.map(updateMessageStatus));

            Toast.success(
                `${updatedMessage.name} was ${statusName}d successfully`,
            );
        } catch (error) {
            console.error('Failed to change notification status', error);
            Toast.error(`Failed to update message ${message.name}`);
        } finally {
            setTogglingMessageId(null);
        }
    };

    const deleteMessages = async () => {
        const selectedRows = [...rowsClicked];
        setIsLoading(true);

        try {
            for (const row of selectedRows) {
                const messageToDelete = messages.find(
                    message => message.id === row,
                );
                if (!messageToDelete) continue;

                try {
                    await deleteMessage(null, messageToDelete.id);
                    Toast.success(
                        `Message with name '${messageToDelete.name}' was deleted successfully.`,
                    );
                } catch (error) {
                    console.error(
                        `Failed to delete message '${messageToDelete.name}'`,
                        error,
                    );
                    Toast.error(
                        `Failed to delete message '${messageToDelete.name}'`,
                    );
                }
            }

            await loadMessages();
        } finally {
            setIsLoading(false);
            deselectRows(selectedRows);
        }
    };

    const duplicateMessages = async () => {
        const existingNames = messages.map(message => message.name);
        const selectedRows = [...rowsClicked];
        setIsLoading(true);

        try {
            for (const row of selectedRows) {
                const messageToDuplicate = messages.find(
                    message => message.id === row,
                );
                if (!messageToDuplicate) continue;

                try {
                    const response = await getMessage(
                        null,
                        messageToDuplicate.id,
                    );
                    const duplicateName = getNameForDuplicate(
                        response.name,
                        existingNames,
                    );
                    existingNames.push(duplicateName);

                    const duplicatedMessage = {
                        ...response,
                        name: duplicateName,
                        notificationSchedules: [],
                    };
                    delete duplicatedMessage.createdBy;
                    delete duplicatedMessage.createdAt;
                    delete duplicatedMessage.updatedBy;
                    delete duplicatedMessage.updatedAt;
                    delete duplicatedMessage.id;

                    await addMessage(null, duplicatedMessage);
                    Toast.success(
                        `Message with name '${messageToDuplicate.name}' was duplicated successfully.`,
                    );
                } catch (error) {
                    console.error(
                        `Failed to duplicate message '${messageToDuplicate.name}'`,
                        error,
                    );
                    Toast.error(getText('msg_10614914d0c2'));
                }
            }

            await loadMessages();
        } finally {
            setIsLoading(false);
            deselectRows(selectedRows);
        }
    };

    const deselectRows = rowsClicked => {
        rowsClicked.forEach(row => {
            const messageElement = document.getElementById(row);
            if (messageElement) {
                messageElement.checked = false;
            }
        });
        setRowsClicked([]);
    };

    const rowAction = action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete':
                    showModal({
                        type: ModalTypes.DELETE_CONFIRMATION,
                        title: getText('msg_b957efeb6067'),
                        text: getText('msg_bd805caecd74'),
                        onYes: deleteMessages,
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_f6fdbe48dc54'),
                    });
                    logEvent(
                        LogEventType.DeleteClicked,
                        `Inbox message delete clicked`,
                    );
                    break;
                case 'duplicate':
                    duplicateMessages();
                    break;
                default:
                    break;
            }
        } else {
            Toast.error(getText('msg_9bcf67dc54d5'));
        }
    };

    const hasScheduleHistory = message =>
        message.hasScheduleHistory ??
        message.HasScheduleHistory ??
        Boolean(message.scheduledFor);

    const toDisplayDate = inputDate =>
        inputDate instanceof Date ? inputDate : parseISO(inputDate);

    const formatShortDate = inputDate => {
        if (!inputDate) return '';
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return formatInTimeZone(
            toDisplayDate(inputDate),
            userTimeZone,
            'MM-dd-yyyy h:mm a',
        );
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex flex-column mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="float-start d-flex">
                                <p className="page-title card-title ms-4 me-5">
                                    {getText('msg_753a22b2eb61')}
                                </p>
                            </div>
                            <div className="float-end d-flex align-items-center">
                                {hasViewerDataFilter && (
                                    <span className="mx-3">
                                        <AudienceBadge
                                            label={viewerAudienceLabel}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>

                        <div
                            id="custom-notifications-panel"
                            role="tabpanel"
                            aria-label={getText('msg_1e0b1d3df0c0')}
                        >
                            <p className="ing-portals-subtitle ms-4 mb-2">
                                {getText('msg_46cbd2d34ee0')}
                                <br />
                                {getText('msg_5d77b298b830')}
                            </p>
                        </div>

                        <div
                            className={styles.tabList}
                            role="tablist"
                            aria-label="Notification views"
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected="true"
                                aria-controls="custom-notifications-panel"
                                className={`${styles.tabItem} ${styles.active}`}
                            >
                                {getText('msg_081ae3fdc403')}
                            </button>

                            <button
                                type="button"
                                role="tab"
                                aria-selected="false"
                                aria-controls="notification-templates-panel"
                                className={styles.tabItem}
                                onClick={() =>
                                    navigate(ROUTES.MESSAGES_TEMPLATES)
                                }
                            >
                                {getText('msg_f25b700ed9f0')}
                            </button>
                        </div>

                        <br />
                        <br />

                        <div className={`${styles.toolbarRow} ms-4 me-4 mb-3`}>
                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={getText('msg_731483f48c21')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <span className={styles.searchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>

                            <div className={styles.toolbarActions}>
                                <span
                                    type="button"
                                    onClick={() => rowAction('delete')}
                                    className="ing-text-buttons"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                                <span
                                    type="button"
                                    onClick={() => rowAction('duplicate')}
                                    className="ing-text-buttons"
                                >
                                    <i className="bi bi-copy me-2" />
                                    {getText('msg_972d57379db3')}
                                </span>
                                <span
                                    type="button"
                                    onClick={clearFilters}
                                    className="ing-text-buttons"
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_381c4bf1d409')}
                                </span>
                                <span
                                    onClick={() =>
                                        navigate(ROUTES.MESSAGES_ADD)
                                    }
                                    type="button"
                                    className="button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle w-100">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center">
                                        <svg
                                            width="19"
                                            height="19"
                                            viewBox="0 0 19 19"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.2 9.3H12.8"
                                                stroke="#0070FF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="17"
                                                height="17"
                                                rx="4"
                                                stroke="#0070FF"
                                            />
                                        </svg>
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() => handleSort('name')}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}{' '}
                                            {renderSortArrow('name')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('name');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() => handleSort('subject')}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_8d183dbdcea3')}{' '}
                                            {renderSortArrow('subject')}
                                        </span>
                                    </th>

                                    {showAudienceColumn && (
                                        <th className="position-relative">
                                            <span className="table-column-header-text">
                                                {getText('msg_51d9934555e2')}
                                            </span>
                                        </th>
                                    )}

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            handleSort('notificationCategory')
                                        }
                                        className="position-relative"
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_a3c686e711e4')}{' '}
                                            {renderSortArrow(
                                                'notificationCategory',
                                            )}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal(
                                                    'notificationCategory',
                                                );
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal ===
                                            'notificationCategory' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() =>
                                            handleSort('nextScheduled')
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_20063ad90533')}{' '}
                                            {renderSortArrow('nextScheduled')}
                                        </span>
                                    </th>

                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_0a8adac9d6d5')}
                                        </span>
                                    </th>

                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_20063ad90532')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMessages.length === 0 && (
                                    <EmptyTableRow
                                        colSpan={showAudienceColumn ? 8 : 7}
                                        title={getText('msg_aca14ec90fb5')}
                                        description={getText(
                                            'msg_519d3b678d87',
                                        )}
                                    />
                                )}
                                {sortedMessages.map(message => (
                                    <tr
                                        key={message.id}
                                        className={`${
                                            rowsClicked.includes(message.id)
                                                ? 'clicked-class'
                                                : ''
                                        }`}
                                    >
                                        <td className="text-center">
                                            <input
                                                id={message.id}
                                                onClick={() =>
                                                    handlerRowClicked(
                                                        message.id,
                                                    )
                                                }
                                                className="form-check-input"
                                                type="checkbox"
                                            />
                                        </td>
                                        <td
                                            className={styles.titleCell}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                navigate(
                                                    ROUTE_BUILDERS.messagesView(
                                                        message.id,
                                                    ),
                                                )
                                            }
                                            onKeyDown={e => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    navigate(
                                                        ROUTE_BUILDERS.messagesView(
                                                            message.id,
                                                        ),
                                                    );
                                                }
                                            }}
                                        >
                                            {message.name}
                                        </td>
                                        <td className="item-page-changed-by">
                                            {message.subject || '-'}
                                        </td>
                                        {showAudienceColumn && (
                                            <td className="item-page-changed-by">
                                                {message.dataFilter ? (
                                                    <AudienceBadge
                                                        label={
                                                            message.dataFilter
                                                        }
                                                        showIcon={false}
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        )}
                                        <td className="item-page-changed-by">
                                            {message.notificationCategory}
                                        </td>
                                        <td className="item-page-changed-by">
                                            {formatShortDate(
                                                getNextScheduledDate(message),
                                            )}
                                        </td>
                                        <td className="text-center">
                                            {hasScheduleHistory(message) ? (
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.scheduleHistoryIconButton
                                                    }
                                                    aria-label={getText(
                                                        'msg_3f433ee27b7e',
                                                        {
                                                            messageName:
                                                                message.name,
                                                        },
                                                    )}
                                                    title={getText(
                                                        'msg_5a7d6cb8ae4a',
                                                    )}
                                                    onClick={() =>
                                                        navigate(
                                                            ROUTE_BUILDERS.notificationScheduleList(
                                                                message.id,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.scheduleHistoryIcon
                                                        }
                                                        aria-hidden="true"
                                                    >
                                                        <svg
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <circle
                                                                cx="12"
                                                                cy="12"
                                                                r="12"
                                                                fill="#007bff"
                                                            />
                                                            <path
                                                                d="M9 15L15 9"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M9 9h6v6"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </button>
                                            ) : null}
                                        </td>
                                        <td className="text-center">
                                            <div className={`form-switch`}>
                                                <input
                                                    id={`notification-status-${message.id}`}
                                                    onChange={() =>
                                                        onToggle(message.id)
                                                    }
                                                    aria-label={getText(
                                                        message.notificationStatus ===
                                                            'Active'
                                                            ? 'msg_966cc30042b6'
                                                            : 'msg_764182ed151b',
                                                        {
                                                            messageName:
                                                                message.name,
                                                        },
                                                    )}
                                                    disabled={
                                                        togglingMessageId ===
                                                        message.id
                                                    }
                                                    className="form-check-input custom-switch-input"
                                                    type="checkbox"
                                                    checked={
                                                        message.notificationStatus ===
                                                        'Active'
                                                    }
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessageList;
